import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { logError } from '@/lib/logger';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to include credentials (cookies) with all requests
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Keep token in memory only for API clients that need Authorization header
  // The primary auth mechanism is now httpOnly cookies
  const [token, setToken] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      // Cookies are automatically sent with credentials: true
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      // Not authenticated or session expired
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent Google OAuth, /auth/callback will exchange
    // the session_id and call login() itself. Skip the /me check so we don't
    // race and flash a logged-out state.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // On mount, check if user is authenticated via cookie
    fetchUser();
  }, [fetchUser]);

  const login = useCallback((tokenOrEmail, userOrPassword) => {
    // If first arg looks like a JWT token (starts with 'ey'), it's a direct login
    // Keep token in memory for backward compatibility with API calls that need header
    if (typeof tokenOrEmail === 'string' && tokenOrEmail.startsWith('ey')) {
      const access_token = tokenOrEmail;
      const userData = userOrPassword;
      setToken(access_token);
      setUser(userData);
      return userData;
    }
    return null;
  }, []);

  const signup = useCallback(async (email, password, name, preferred_name, referral_code = null) => {
    const response = await axios.post(`${API}/auth/signup`, {
      email,
      password,
      name,
      preferred_name,
      referral_code,
    });
    const { access_token, user: userData } = response.data;
    // Cookie is set by backend, keep token in memory for API compatibility
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend to clear httpOnly cookie
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore errors - clear local state regardless
      logError('Logout request failed:', error);
    }
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    login,
    signup,
    logout,
    token,
    refreshUser
  }), [user, loading, login, signup, logout, token, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
